import { Chat } from "@ai-sdk/react";
import { asSchema, ChatInit, ChatStatus, DefaultChatTransport, isToolUIPart, ToolSet } from "ai";
import Bugsnag from "@bugsnag/js";

import useChatStore from "./store/useStore";
import { ChatToolPayload, ChatUIMessage } from "./types";
import { McpToolSet } from "../types";
import { selectChatTools } from "./store/selectors";

export class ConversationConflictError extends Error {
  constructor() {
    super(
      "This conversation was updated elsewhere and has been reloaded. Please send your message again."
    );
    this.name = "ConversationConflictError";
  }
}

type ChatErrorType = "conflict" | "stream" | "tool";

export const notifyChatError = (error: Error, errorType: ChatErrorType) => {
  if (!Bugsnag.isStarted()) return;

  const { chat, mode, conversationVersion, integrationId, model } = useChatStore.getState();

  Bugsnag.notify(error, (event) => {
    event.context = "InfraAssistant";
    event.groupingHash = `infra-assistant-${errorType}`;
    event.severity = "error";
    event.addMetadata("infraAssistant", {
      errorType,
      chatId: chat?.id,
      mode,
      conversationVersion,
      integrationId,
      model,
    });
  });
};

const stripSchemaMeta = (schema: Record<string, unknown>) => {
  const stripped = { ...schema };
  delete stripped.$schema;
  delete stripped.type;
  delete stripped.additionalProperties;
  return stripped;
};

export const processTools = (tools: ToolSet | McpToolSet): ChatToolPayload[] => {
  return Object.entries(tools).map(([name, tool]) => ({
    title: tool.title,
    name,
    description: tool.description ?? "",
    ...(tool.needsApproval === true ? { needsApproval: true } : {}),
    schema: stripSchemaMeta(asSchema(tool.inputSchema).jsonSchema as Record<string, unknown>),
  }));
};

export const lastAssistantMessageIsSettled = ({ messages }: { messages: ChatUIMessage[] }) => {
  const message = messages[messages.length - 1];
  if (!message || message.role !== "assistant") {
    return false;
  }

  const lastStepStartIndex = message.parts.reduce(
    (lastIndex, part, index) => (part.type === "step-start" ? index : lastIndex),
    -1
  );
  const lastStepToolParts = message.parts
    .slice(lastStepStartIndex + 1)
    .filter(isToolUIPart)
    .filter((part) => !part.providerExecuted);

  return (
    lastStepToolParts.length > 0 &&
    lastStepToolParts.every(
      (part) =>
        part.state === "output-available" ||
        part.state === "output-error" ||
        part.state === "output-denied" ||
        (part.state === "approval-responded" && part.approval.approved === false)
    )
  );
};

type CreateChatTransportOptions = {
  chatId: string;
};

export const createChatTransport = ({ chatId }: CreateChatTransportOptions) =>
  new DefaultChatTransport({
    api: `/intent/chat/${chatId}`,
    credentials: "include",
    fetch: async (url, init) => {
      const response = await globalThis.fetch(url as RequestInfo, init as RequestInit);
      if (response.status === 409) {
        throw new ConversationConflictError();
      }
      return response;
    },
    prepareSendMessagesRequest({ body, messages }) {
      const lastMessage = messages[messages.length - 1];

      return {
        body: {
          ...body,
          // Tools must ride on every request (not just the user's typed message):
          // auto-continuations after an approval pause carry toolResult blocks, and
          // Bedrock rejects those without a matching toolConfig.
          tools: selectChatTools(
            useChatStore.getState().mcpFilteredTools,
            useChatStore.getState().frontendTools
          ),
          integrationId: useChatStore.getState().integrationId,
          model: useChatStore.getState().model,
          conversationVersion: useChatStore.getState().conversationVersion,
          mode: useChatStore.getState().mode,
          messages: [lastMessage],
        },
      };
    },
  });

export const isChatStatusBusy = (status: ChatStatus | undefined) =>
  status === "streaming" || status === "submitted";

type ChatCallbacks = Pick<
  ChatInit<ChatUIMessage>,
  "onToolCall" | "onData" | "onFinish" | "onError"
>;

// Single slot: exactly one Content is mounted at a time (drawer XOR page), see Layout/index.tsx.
export const chatCallbacks: { current: ChatCallbacks } = { current: {} };

export const createChat = (id: string, messages?: ChatUIMessage[]) => {
  // Events from an instance the store has already replaced must not reach the mounted Content.
  const isCurrent = () => useChatStore.getState().chat === chat;

  const chat: Chat<ChatUIMessage> = new Chat<ChatUIMessage>({
    id,
    messages,
    transport: createChatTransport({ chatId: id }),
    sendAutomaticallyWhen: lastAssistantMessageIsSettled,
    onToolCall: (options) =>
      isCurrent() ? chatCallbacks.current.onToolCall?.(options) : undefined,
    onData: (dataPart) => {
      if (isCurrent()) chatCallbacks.current.onData?.(dataPart);
    },
    onFinish: (options) => {
      if (isCurrent()) chatCallbacks.current.onFinish?.(options);
    },
    onError: (error) => {
      if (isCurrent()) chatCallbacks.current.onError?.(error);
    },
  });

  return chat;
};

export const createNewChatPlaceholder = () =>
  new Chat<ChatUIMessage>({
    transport: {
      sendMessages: () => Promise.reject(new Error("No conversation started")),
      reconnectToStream: () => Promise.resolve(null),
    },
  });
